import React, { FC, useEffect, useState } from 'react';
import { Col, Form, Input, Row, Select, Spin, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { i18nKey } from 'src/locales/i18n';
import styles from './contact-person.module.less';
import TitleBox from 'src/components/title-box/title-box';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { CHINA_NATIONALITY_VALUE, DEAFULT_COUNTRY_CODE_CHINESE, HONG_KONG_NATIONALITY_VALUE, ONBOARDING_CURRENCY_TYPE, OTHER_VALUE_TYPE } from 'src/constants';
import useStore from 'src/hooks/useStore';
import { IApplicationStore } from 'src/store/root/application.store';

import { ICorporateOnboardingStore } from 'src/store/corporate/onboarding.store';
import { observer } from 'mobx-react-lite';
import { scrollPageToTop } from 'src/helpers/window';
import NumberInput from 'src/components/input-with-number/input-with-number';
import { normalizeInputBlockNumber, normalizeInputBlockNumberAndAllowChineseCharacter } from 'src/helpers/common.utils';
import { CHINESE_MOBILE_LENGTH, LETTER_REGEX } from 'src/constants/validation';
import { checkChineseCharacters, checkLengthCharacters } from 'src/utils/common.utils';

const CorporateBorrowerContactPerson: FC = () => {
	const applicationStore: IApplicationStore = useStore('applicationStore');
	const corporateOnboardingStore: ICorporateOnboardingStore = useStore('corporateOnboardingStore');

	const [isChineseNationality, setChineseNationality] = useState<boolean>(false);

	const [t] = useTranslation();

	const [accountForm] = Form.useForm();

	const onChangeNationality = (value: string) => {
		switch (value) {
			case HONG_KONG_NATIONALITY_VALUE:
				setChineseNationality(false);
				break;
			case CHINA_NATIONALITY_VALUE:
				setChineseNationality(true);
				accountForm.setFieldValue('gender', undefined);
				accountForm.setFieldValue('mobilePhoneCountryCode', DEAFULT_COUNTRY_CODE_CHINESE);
				break;
			default:
				setChineseNationality(false);
				accountForm.setFieldValue('mobilePhoneCountryCode', null);
				break;
		}
	};

	const onChangeTitle = (title: string) => {
		accountForm.resetFields(['gender']);
		corporateOnboardingStore.updateContactPerson({title})
	}

	const renderTitle = () => {
		if (corporateOnboardingStore.contractPerson?.title === OTHER_VALUE_TYPE.OTHERS_VALUE) {
			return (
				<>
					<Col xs={12} sm={12} md={12} lg={12}>
						<Form.Item label={`${t(i18nKey.dashboard.accountInformation.title)}`}>
							<Row gutter={10}>
								<Col span={12}>
									<Form.Item noStyle name="title">
										<Select placeholder={`${t(i18nKey.common.select)}`} options={applicationStore.titleOptions} onChange={onChangeTitle}/>
									</Form.Item>
								</Col>
								<Col span={12}>
									<Form.Item name="titleOther">
										<Input />
									</Form.Item>
								</Col>
							</Row>
						</Form.Item>
					</Col>
				</>
			);
		}

		return (
			<>
				<Col xs={24} sm={24} md={24} lg={12}>
					<Form.Item name="title" label={`${t(i18nKey.dashboard.accountInformation.title)}`}>
						<Select placeholder={`${t(i18nKey.common.select)}`} options={applicationStore.titleOptions} onChange={onChangeTitle}/>
					</Form.Item>
				</Col>
			</>
		);
	};

	useEffect(() => {
		scrollPageToTop();
	}, []);

	useEffect(() => {
		if (!accountForm.isFieldsTouched()) {
			accountForm.setFieldsValue({
				...corporateOnboardingStore.contractPerson,
				referenceCurrency:
					corporateOnboardingStore.contractPerson && corporateOnboardingStore.contractPerson.referenceCurrency
						? corporateOnboardingStore.contractPerson.referenceCurrency
						: ONBOARDING_CURRENCY_TYPE.HKD
			});
		}
	}, [corporateOnboardingStore.contractPerson]);

	return (
		<>
			<Form
				form={accountForm}
				layout="vertical"
				disabled
				initialValues={{
					...corporateOnboardingStore.contractPerson,
					referenceCurrency:
						corporateOnboardingStore.contractPerson && corporateOnboardingStore.contractPerson.referenceCurrency
							? corporateOnboardingStore.contractPerson.referenceCurrency
							: ONBOARDING_CURRENCY_TYPE.HKD
				}}>
				<TitleBox>
					<span>{t(i18nKey.onboarding.corporate.form.contactPersonStep)}</span>
				</TitleBox>
				<Row align="middle" gutter={[0, 0]}>
					<Col span={24}>
						<Typography.Text className={styles.titleSection}>
							<ExclamationCircleFilled
								style={{
									color: '#22A5B4',
									cursor: 'pointer',
									paddingLeft: '0.5rem',
									fontSize: '2rem'
								}}
							/>
							<span>{t(i18nKey.onboarding.corporate.form.notice)}</span>
						</Typography.Text>
					</Col>
				</Row>
				<Spin spinning={false}>
					<div className="common-padding">
						<div className={styles.subLevelForm}>
							<Row gutter={[32, 16]}>
								{renderTitle()}
								<Col xs={24} sm={24} md={24} lg={12}>
									<Form.Item
										name="firstName"
										label={`${t(i18nKey.registration.firstName)}`}
										required={true}
										normalize={normalizeInputBlockNumber}
										rules={[
											{
												required: true,
												message: `${t(i18nKey.onboarding.validation.required)}`
											},
											{
												pattern: LETTER_REGEX,
												message: `${t(i18nKey.registration.validation.inputOnlyAlphabet)}`
											}
										]}>
										<Input placeholder="-" />
									</Form.Item>
								</Col>
							</Row>
							<Row gutter={[32, 16]}>
								<Col xs={24} sm={24} md={24} lg={12}>
									<Form.Item
										name="middleName"
										normalize={normalizeInputBlockNumber}
										label={`${t(i18nKey.registration.middleName)}`}
										rules={[
											{
												pattern: LETTER_REGEX,
												message: `${t(i18nKey.registration.validation.inputOnlyAlphabet)}`
											}
										]}>
										<Input placeholder="-" />
									</Form.Item>
								</Col>
								<Col xs={24} sm={24} md={24} lg={12}>
									<Form.Item
										name="lastName"
										label={`${t(i18nKey.registration.lastName)}`}
										required={true}
										normalize={normalizeInputBlockNumber}
										rules={[
											{
												required: true,
												message: `${t(i18nKey.onboarding.validation.required)}`
											},
											{
												pattern: LETTER_REGEX,
												message: `${t(i18nKey.registration.validation.inputOnlyAlphabet)}`
											}
										]}>
										<Input placeholder="-" />
									</Form.Item>
								</Col>
							</Row>

							<Row gutter={[32, 16]}>
								<Col xs={24} sm={24} md={24} lg={12}>
									<Form.Item
										name="nationality"
										label={`${t(i18nKey.dashboard.accountInformation.nationality)}`}
										required={true}
										rules={[
											{
												required: true,
												message: `${t(i18nKey.onboarding.validation.required)}`
											}
										]}>
										<Select
											onChange={onChangeNationality}
											placeholder={`${t(i18nKey.common.select)}`}
											options={applicationStore.nationalities}
											showSearch
											filterOption={(input, option) =>
												new RegExp(`^${input.toLowerCase().trim()}`).test((option?.label ?? '').toLowerCase())
											}
										/>
									</Form.Item>
								</Col>
								<Col xs={24} sm={24} md={24} lg={12}>
									<Form.Item
										name="chineseName"
										normalize={normalizeInputBlockNumberAndAllowChineseCharacter}
										label={`${t(i18nKey.dashboard.accountInformation.chineseName)}`}
										rules={[
											{
												validator: checkChineseCharacters
											}
										]}>
										<Input />
									</Form.Item>
								</Col>
							</Row>

							<Row gutter={[32, 16]}>
								<Col xs={24} sm={24} md={24} lg={12}>
									<Form.Item
										name="gender"
										label={`${t(i18nKey.dashboard.accountInformation.gender)}`}
										required={true}
										rules={[
											{
												required: true,
												message: `${t(i18nKey.onboarding.validation.required)}`
											}
										]}>
										<Select
											placeholder={`${t(i18nKey.common.select)}`}
											allowClear
											options={
												applicationStore.filterGenderByTitle(isChineseNationality, corporateOnboardingStore.contractPerson?.title as string)
											}
										/>
									</Form.Item>
								</Col>
								<Col xs={24} sm={24} md={24} lg={12}>
									<Form.Item label={`${t(i18nKey.dashboard.accountInformation.officePhoneNo)}`}>
										<Row gutter={[16, 16]}>
											<Col xs={9} sm={9} md={6} lg={11} xl={9} xxl={7}>
												<Form.Item
													noStyle
													name={'officePhoneCountryCode'}
													rules={[
														{
															required: true,
															message: `${t(i18nKey.onboarding.validation.required)}`
														}
													]}>
													<Select
														placeholder={`${t(i18nKey.common.select)}`}
														options={applicationStore.phoneCountryCode}
														showSearch
														filterOption={(input, option) =>
															(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
														}
													/>
												</Form.Item>
											</Col>
											<Col xs={15} sm={15} md={18} lg={13} xl={15} xxl={17}>
												<NumberInput
													name="officePhone"
													rules={[
														{
															required: true,
															message: `${t(i18nKey.onboarding.validation.required)}`
														}
													]}
												/>
											</Col>
										</Row>
									</Form.Item>
								</Col>
								<Col xs={24} sm={24} md={24} lg={12}>
									<Form.Item required={true} label={`${t(i18nKey.dashboard.accountInformation.mobilePhone)}`}>
										<Row gutter={[16, 16]}>
											<Col xs={9} sm={9} md={6} lg={11} xl={9} xxl={7}>
												<Form.Item
													noStyle
													name={'mobilePhoneCountryCode'}
													required={false}
													rules={[
														{
															required: true,
															message: `${t(i18nKey.onboarding.validation.required)}`
														}
													]}>
													<Select
														placeholder={`${t(i18nKey.common.select)}`}
														options={applicationStore.phoneCountryCode}
														showSearch
														disabled={isChineseNationality}
														filterOption={(input, option) =>
															(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
														}
													/>
												</Form.Item>
											</Col>
											<Col xs={15} sm={15} md={18} lg={13} xl={15} xxl={17}>
												<NumberInput
													name={'mobilePhone'}
													rules={[
														{
															required: true,
															message: `${t(i18nKey.onboarding.validation.required)}`
														},
														() => ({
															validator(_, value) {
																return checkLengthCharacters(value, CHINESE_MOBILE_LENGTH, isChineseNationality);
															}
														})
													]}
												/>
											</Col>
										</Row>
									</Form.Item>
								</Col>
								<Col xs={24} sm={24} md={24} lg={12}>
									<Form.Item
										name="positionInCompany"
										label={`${t(i18nKey.dashboard.accountInformation.positionInCompany)}`}
										required={true}
										normalize={normalizeInputBlockNumber}
										rules={[
											{
												required: true,
												message: `${t(i18nKey.onboarding.validation.required)}`
											}
										]}>
										<Input />
									</Form.Item>
								</Col>
							</Row>
						</div>
					</div>
				</Spin>
			</Form>
		</>
	);
};

export default observer(CorporateBorrowerContactPerson);
